import { createContext, useContext, useEffect, useRef, useState } from "react";
import config from "../config/config";
import authHeader from "../config/authHeader";
import { useAuth } from "./AuthContext";

const EnumCtx = createContext(null);
const CACHE_KEY = "enums:v1";
const CACHE_TTL_MS = 1000 * 60 * 30; // 30 min

export function EnumProvider({ children }) {
    const { token, logout } = useAuth();
    const [enums, setEnums] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const isFetchingRef = useRef(false);

    const readCache = () => {
        try {
            const raw = sessionStorage.getItem(CACHE_KEY);
            if (!raw) return null;
            const { at, data } = JSON.parse(raw);
            if (!at || !data) return null;
            const fresh = Date.now() - at < CACHE_TTL_MS;
            return { data, fresh };
        } catch {
            return null;
        }
    };

    const writeCache = (data) => {
        try {
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
        } catch { }
    };

    const fetchEnums = async ({ background = false } = {}) => {
        if (!token || isFetchingRef.current) return;
        isFetchingRef.current = true;
        if (!background) { setLoading(true); setError(""); }

        try {
            const res = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getallEnums`, {
                method: "GET",
                headers: authHeader(),
            });

            if (res.status === 401) {
                logout();
                return;
            }
            if (!res.ok) throw new Error("Erreur réseau lors de la récupération des enums");

            const data = await res.json();
            setEnums(data);
            writeCache(data);
        } catch (e) {
            setError(String(e.message || e));
        } finally {
            isFetchingRef.current = false;
            if (!background) setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) { setEnums(null); return; }
        const cached = readCache();
        if (cached?.data) setEnums(cached.data);
        if (!cached || !cached.fresh) fetchEnums({ background: !cached });
        else fetchEnums({ background: true });
    }, [token]);

    const value = {
        enums, loading, error,
        refreshEnums: () => fetchEnums({ background: false }),
    };

    return <EnumCtx.Provider value={value}>{children}</EnumCtx.Provider>;
}

export const useEnums = () => {
    const ctx = useContext(EnumCtx);
    if (!ctx) throw new Error("useEnums must be used within <EnumProvider>");
    return ctx;
};
